import React, { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Download, ChevronLeft, ChevronRight } from "lucide-react";

export const ImageLightbox = ({ items, index, onClose, onPrev, onNext, downloadLabel }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNext && onNext();
      if (e.key === "ArrowLeft") onPrev && onPrev();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onNext, onPrev]);

  const active = items[index];
  if (!active) return null;
  const src = typeof active === "string" ? active : active.src;
  const caption = typeof active === "string" ? null : active.caption;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        data-testid="lightbox-overlay"
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 z-10 h-11 w-11 flex items-center justify-center border border-white/20 text-[#FFFFF0] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          data-testid="lightbox-close"
          aria-label="Close"
        >
          <X size={20} strokeWidth={1.4} />
        </button>

        {onPrev && (
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute start-4 z-10 h-12 w-12 flex items-center justify-center border border-white/15 text-[#FFFFF0] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            data-testid="lightbox-prev"
            aria-label="Previous"
          >
            <ChevronLeft size={22} strokeWidth={1.4} />
          </button>
        )}
        {onNext && (
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute end-4 z-10 h-12 w-12 flex items-center justify-center border border-white/15 text-[#FFFFF0] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            data-testid="lightbox-next"
            aria-label="Next"
          >
            <ChevronRight size={22} strokeWidth={1.4} />
          </button>
        )}

        <motion.div
          className="relative max-w-6xl w-full"
          initial={{ scale: 0.96, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.96, opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={src}
            alt={caption || "Phantom Residence"}
            className="w-full max-h-[82vh] object-contain"
            data-testid="lightbox-image"
          />
          <div className="mt-4 flex items-center justify-between gap-4">
            {caption ? (
              <p className="text-sm text-[#FFFFF0]/80 font-light">{caption}</p>
            ) : <span />}
            <a
              href={src}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#D4AF37] hover:text-[#FFFFF0] transition-colors"
              data-testid="lightbox-download"
            >
              <Download size={15} strokeWidth={1.5} /> {downloadLabel || "Download"}
            </a>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
